import { Container, Graphics, Rectangle, Text, type FederatedPointerEvent } from 'pixi.js';
import { DEFAULT_SETTINGS } from '../app/GameState';
import type { GameConfig } from '../app/GameConfig';
import type { GameSettings } from '../app/types';
import type { ViewportLayout } from '../app/Viewport';
import { BaseScene, type SceneServices } from './BaseScene';
import {
  Button,
  HeaderBar,
  Modal,
  Panel,
  UI_COLORS,
  UI_FONT_FAMILY,
  VolumeSlider,
  type VolumeSliderChannel
} from '../ui';

export interface SettingsSceneOptions {
  onBack?: () => void;
  onOpenEvidence?: () => void;
  onOpenCaseList?: () => void;
  onGoToTitle?: () => void;
}

type NumericSettingKey = 'textSpeed' | 'autoSpeed';
type ToggleSettingKey =
  | 'skipReadText'
  | 'reduceMotion'
  | 'lightweightEffects'
  | 'screenShake'
  | 'visualEffects';

const PANEL_WIDTH = 980;
const PANEL_HEIGHT = 560;
const SLIDER_WIDTH = 430;

export class SettingsScene extends BaseScene {
  private readonly background = new Graphics();
  private readonly titleText = new Text({
    text: '設定',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 34,
      fontWeight: '900'
    }
  });
  private readonly subtitleText = new Text({
    text: '調査ファイルの再生環境',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '700'
    }
  });
  private readonly headerBar = new HeaderBar({
    width: 520,
    disabledActions: ['settings'],
    onAction: (action) => this.handleHeaderAction(action)
  });
  private readonly backButton = new Button({
    label: '戻る',
    width: 150,
    height: 48,
    variant: 'ghost',
    onPress: () => this.navigateAfterCurrentPointer(() => this.options.onBack?.())
  });
  private readonly resetButton = new Button({
    label: '初期値に戻す',
    width: 180,
    height: 48,
    variant: 'danger',
    onPress: () => this.confirmReset()
  });
  private readonly panel = new Panel({
    width: PANEL_WIDTH,
    height: PANEL_HEIGHT,
    title: '環境設定'
  });
  private readonly audioHeading = createHeading('音量');
  private readonly textHeading = createHeading('表示');
  private readonly statusText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 17,
      fontWeight: '800'
    }
  });
  private readonly sliders = new Map<VolumeSliderChannel, VolumeSlider>();
  private readonly steppers = new Map<NumericSettingKey, NumericStepper>();
  private readonly toggles = new Map<ToggleSettingKey, ToggleSwitch>();
  private readonly modalLayer = new Container();
  private layout: ViewportLayout | null = null;
  private noticeModal: Modal | null = null;

  public constructor(
    services: SceneServices,
    private readonly config: GameConfig,
    private readonly options: SettingsSceneOptions = {}
  ) {
    super(services);
  }

  protected override onStart(): void {
    this.container.addChild(this.background);
    this.container.addChild(this.titleText);
    this.container.addChild(this.subtitleText);
    this.container.addChild(this.headerBar);
    this.container.addChild(this.backButton);
    this.container.addChild(this.panel);
    this.container.addChild(this.modalLayer);
    this.createControls();
    this.refreshControls();
    this.resize(this.services.getLayout());
  }

  protected override onDestroy(): void {
    this.destroyNoticeModal();
  }

  public override resize(layout: ViewportLayout): void {
    this.layout = layout;
    this.drawBackground(layout.baseWidth, layout.baseHeight);
    this.titleText.position.set(layout.safeArea.x, layout.safeArea.y + 16);
    this.subtitleText.position.set(layout.safeArea.x + 4, layout.safeArea.y + 60);
    this.headerBar.position.set(layout.safeArea.x + layout.safeArea.width - 520, layout.safeArea.y);
    this.panel.position.set(Math.round((layout.baseWidth - PANEL_WIDTH) / 2), 120);
    this.backButton.position.set(
      layout.safeArea.x,
      layout.safeArea.y + layout.safeArea.height - 58
    );
    this.resetButton.position.set(PANEL_WIDTH - 236, PANEL_HEIGHT - 68);
    this.noticeModal?.setViewportSize(layout.baseWidth, layout.baseHeight);
  }

  private createControls(): void {
    this.panel.content.addChild(this.audioHeading);
    this.audioHeading.position.set(0, 0);

    const settings = this.services.getState().getSettings();
    const sliderConfigs: Array<{
      channel: VolumeSliderChannel;
      label: string;
      value: number;
      x: number;
      y: number;
    }> = [
      { channel: 'master', label: 'Master', value: settings.masterVolume, x: 0, y: 42 },
      { channel: 'bgm', label: 'BGM', value: settings.bgmVolume, x: 0, y: 94 },
      { channel: 'se', label: 'SE', value: settings.seVolume, x: 0, y: 146 },
      { channel: 'voice', label: 'Voice', value: settings.voiceVolume, x: 0, y: 198 }
    ];

    for (const config of sliderConfigs) {
      const slider = new VolumeSlider({
        channel: config.channel,
        label: config.label,
        value: config.value,
        width: SLIDER_WIDTH,
        onChange: (value, channel) => this.handleVolumeChange(value, channel)
      });
      slider.position.set(config.x, config.y);
      this.sliders.set(config.channel, slider);
      this.panel.content.addChild(slider);
    }

    this.textHeading.position.set(0, 278);
    this.panel.content.addChild(this.textHeading);

    const textSpeedStepper = new NumericStepper({
      label: '文字速度',
      value: settings.textSpeed,
      onChange: (value) => this.handleNumericSettingChange('textSpeed', value)
    });
    textSpeedStepper.position.set(0, 318);
    this.steppers.set('textSpeed', textSpeedStepper);
    this.panel.content.addChild(textSpeedStepper);

    const autoSpeedStepper = new NumericStepper({
      label: 'オート速度',
      value: settings.autoSpeed,
      onChange: (value) => this.handleNumericSettingChange('autoSpeed', value)
    });
    autoSpeedStepper.position.set(0, 376);
    this.steppers.set('autoSpeed', autoSpeedStepper);
    this.panel.content.addChild(autoSpeedStepper);

    const skipReadToggle = new ToggleSwitch({
      label: '既読スキップ',
      checked: settings.skipReadText,
      onChange: (checked) => this.handleToggleSettingChange('skipReadText', checked)
    });
    skipReadToggle.position.set(560, 248);
    this.toggles.set('skipReadText', skipReadToggle);
    this.panel.content.addChild(skipReadToggle);

    const reduceMotionToggle = new ToggleSwitch({
      label: '演出軽減',
      checked: settings.reduceMotion,
      onChange: (checked) => this.handleToggleSettingChange('reduceMotion', checked)
    });
    reduceMotionToggle.position.set(560, 294);
    this.toggles.set('reduceMotion', reduceMotionToggle);
    this.panel.content.addChild(reduceMotionToggle);

    const lightweightEffectsToggle = new ToggleSwitch({
      label: '軽量演出',
      checked: settings.lightweightEffects,
      onChange: (checked) => this.handleToggleSettingChange('lightweightEffects', checked)
    });
    lightweightEffectsToggle.position.set(560, 340);
    this.toggles.set('lightweightEffects', lightweightEffectsToggle);
    this.panel.content.addChild(lightweightEffectsToggle);

    const screenShakeToggle = new ToggleSwitch({
      label: '画面揺れ',
      checked: settings.screenShake,
      onChange: (checked) => this.handleToggleSettingChange('screenShake', checked)
    });
    screenShakeToggle.position.set(560, 386);
    this.toggles.set('screenShake', screenShakeToggle);
    this.panel.content.addChild(screenShakeToggle);

    const visualEffectsToggle = new ToggleSwitch({
      label: '背景演出',
      checked: settings.visualEffects,
      onChange: (checked) => this.handleToggleSettingChange('visualEffects', checked)
    });
    visualEffectsToggle.position.set(560, 432);
    this.toggles.set('visualEffects', visualEffectsToggle);
    this.panel.content.addChild(visualEffectsToggle);

    this.statusText.position.set(0, PANEL_HEIGHT - 124);
    this.panel.content.addChild(this.statusText);
    this.panel.content.addChild(this.resetButton);
  }

  private refreshControls(): void {
    const settings = this.services.getState().getSettings();
    this.sliders.get('master')?.setValue(settings.masterVolume);
    this.sliders.get('bgm')?.setValue(settings.bgmVolume);
    this.sliders.get('se')?.setValue(settings.seVolume);
    this.sliders.get('voice')?.setValue(settings.voiceVolume);
    this.steppers.get('textSpeed')?.setValue(settings.textSpeed);
    this.steppers.get('autoSpeed')?.setValue(settings.autoSpeed);
    this.toggles.get('skipReadText')?.setChecked(settings.skipReadText);
    this.toggles.get('reduceMotion')?.setChecked(settings.reduceMotion);
    this.toggles.get('lightweightEffects')?.setChecked(settings.lightweightEffects);
    this.toggles.get('screenShake')?.setChecked(settings.screenShake);
    this.toggles.get('visualEffects')?.setChecked(settings.visualEffects);
  }

  private handleVolumeChange(value: number, channel: VolumeSliderChannel): void {
    const audio = this.services.getAudio();
    switch (channel) {
      case 'master':
        audio.setMasterVolume(value);
        break;
      case 'bgm':
        audio.setBgmVolume(value);
        break;
      case 'se':
        audio.setSeVolume(value);
        break;
      case 'voice':
        audio.setVoiceVolume(value);
        break;
    }
    this.showSavedStatus();
  }

  private handleNumericSettingChange(key: NumericSettingKey, value: number): void {
    this.persistSettings({ [key]: value });
  }

  private handleToggleSettingChange(key: ToggleSettingKey, checked: boolean): void {
    this.persistSettings({ [key]: checked });
  }

  private persistSettings(settings: Partial<GameSettings>): void {
    const state = this.services.getState();
    state.updateSettings(settings);
    this.services.getSaveManager().saveSettings(state.getSettings());
    this.refreshControls();
    this.showSavedStatus();
  }

  private confirmReset(): void {
    this.showConfirm(
      '設定リセット',
      '音量、文字速度、オート速度、既読スキップ、演出軽減、軽量演出、画面揺れ、背景演出を初期値に戻します。',
      'リセット',
      () => this.resetSettings()
    );
  }

  private resetSettings(): void {
    const audio = this.services.getAudio();
    audio.setMasterVolume(DEFAULT_SETTINGS.masterVolume);
    audio.setBgmVolume(DEFAULT_SETTINGS.bgmVolume);
    audio.setSeVolume(DEFAULT_SETTINGS.seVolume);
    audio.setVoiceVolume(DEFAULT_SETTINGS.voiceVolume);
    this.persistSettings({
      textSpeed: DEFAULT_SETTINGS.textSpeed,
      autoSpeed: DEFAULT_SETTINGS.autoSpeed,
      skipReadText: DEFAULT_SETTINGS.skipReadText,
      reduceMotion: DEFAULT_SETTINGS.reduceMotion,
      lightweightEffects: DEFAULT_SETTINGS.lightweightEffects,
      screenShake: DEFAULT_SETTINGS.screenShake,
      visualEffects: DEFAULT_SETTINGS.visualEffects
    });
    this.showSavedStatus('初期値に戻しました');
  }

  private handleHeaderAction(
    action: 'save' | 'evidence' | 'caseList' | 'settings' | 'title'
  ): void {
    switch (action) {
      case 'save':
      case 'settings':
        this.headerBar.releaseActionLock();
        break;
      case 'evidence':
        if (this.options.onOpenEvidence) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenEvidence?.());
        } else {
          this.showNotice('証拠品', '証拠品画面はゲーム開始後に確認できます。');
        }
        break;
      case 'caseList':
        if (this.options.onOpenCaseList) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenCaseList?.());
        } else {
          this.showNotice('事件リスト', '事件リストはゲーム開始後に確認できます。');
        }
        break;
	      case 'title':
	        this.navigateAfterCurrentPointer(() => this.options.onBack?.());
	        break;
	    }
	  }

  private showSavedStatus(message = '設定を保存しました'): void {
    this.statusText.text = message;
  }

  private navigateAfterCurrentPointer(navigate: () => void): void {
    this.services.getInput().lock();
    this.headerBar.releaseActionLock();
    try {
      navigate();
    } finally {
      window.setTimeout(() => this.services.getInput().unlock(), 0);
    }
  }

  private showConfirm(
    title: string,
    message: string,
    confirmLabel: string,
    onConfirm: () => void
  ): void {
    this.destroyNoticeModal();
    this.services.getInput().lock();
    const layout = this.layout;
    const modal = new Modal({
      title,
      message,
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: '戻る',
          variant: 'ghost',
          onPress: () => this.destroyNoticeModal()
        },
        {
          label: confirmLabel,
          variant: 'danger',
          onPress: () => {
            this.destroyNoticeModal();
            onConfirm();
          }
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });
    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private showNotice(title: string, message: string): void {
    this.destroyNoticeModal();
    this.services.getInput().lock();
    const layout = this.layout;
    const modal = new Modal({
      title,
      message,
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: '閉じる',
          variant: 'primary',
          onPress: () => this.destroyNoticeModal()
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });
    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private destroyNoticeModal(): void {
    if (!this.noticeModal) {
      return;
    }

    this.noticeModal.destroy({ children: true });
    this.noticeModal = null;
    this.services.getInput().unlock();
    this.headerBar.releaseActionLock();
  }

  private drawBackground(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height).fill(UI_COLORS.black);
    this.background.rect(0, 0, width, height).fill({ color: UI_COLORS.redDark, alpha: 0.2 });
    this.background.rect(0, 0, width, 8).fill({ color: UI_COLORS.red, alpha: 0.84 });
    this.background.rect(0, height - 8, width, 8).fill({ color: UI_COLORS.gold, alpha: 0.72 });
  }
}

interface NumericStepperOptions {
  label: string;
  value: number;
  onChange: (value: number) => void;
}

class NumericStepper extends Container {
  private readonly labelText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      fontWeight: '800'
    }
  });
  private readonly valueText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 20,
      fontWeight: '800'
    }
  });
  private readonly minusButton = new Button({
    label: '-',
    width: 54,
    height: 42,
    variant: 'secondary',
    onPress: () => this.changeBy(-0.1)
  });
  private readonly plusButton = new Button({
    label: '+',
    width: 54,
    height: 42,
    variant: 'secondary',
    onPress: () => this.changeBy(0.1)
  });
  private value: number;

  public constructor(private readonly options: NumericStepperOptions) {
    super();
    this.value = clamp01(options.value);
    this.addChild(this.labelText);
    this.addChild(this.valueText);
    this.addChild(this.minusButton);
    this.addChild(this.plusButton);
    this.labelText.text = options.label;
    this.layout();
    this.draw();
  }

  public setValue(value: number): void {
    this.value = clamp01(value);
    this.draw();
  }

  private changeBy(delta: number): void {
    this.setValue(this.value + delta);
    this.options.onChange(this.value);
  }

  private layout(): void {
    this.labelText.position.set(0, 12);
    this.minusButton.position.set(180, 0);
    this.valueText.position.set(254, 12);
    this.plusButton.position.set(338, 0);
  }

  private draw(): void {
    this.valueText.text = `${Math.round(this.value * 100)}%`;
  }
}

interface ToggleSwitchOptions {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

class ToggleSwitch extends Container {
  private readonly labelText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      fontWeight: '800'
    }
  });
  private readonly stateText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 20,
      fontWeight: '900'
    }
  });
  private readonly track = new Graphics();
  private readonly knob = new Graphics();
  private checked: boolean;

  public constructor(private readonly options: ToggleSwitchOptions) {
    super();
    this.checked = options.checked;
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Rectangle(0, 0, 330, 48);
    this.on('pointertap', this.handleTap);
    this.addChild(this.labelText);
    this.addChild(this.track);
    this.addChild(this.knob);
    this.addChild(this.stateText);
    this.labelText.text = options.label;
    this.layout();
    this.draw();
  }

  public setChecked(checked: boolean): void {
    this.checked = checked;
    this.draw();
  }

  private readonly handleTap = (event: FederatedPointerEvent): void => {
    event.stopPropagation();
    this.setChecked(!this.checked);
    this.options.onChange(this.checked);
  };

  private layout(): void {
    this.labelText.position.set(0, 12);
    this.track.position.set(176, 6);
    this.knob.position.set(176, 6);
    this.stateText.position.set(262, 12);
  }

  private draw(): void {
    this.stateText.text = this.checked ? 'ON' : 'OFF';
    this.track.clear();
    this.track
      .roundRect(0, 0, 72, 34, 17)
      .fill({ color: this.checked ? UI_COLORS.redDark : UI_COLORS.paperLight, alpha: 0.92 })
      .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.72 });
    this.knob.clear();
    this.knob
      .circle(this.checked ? 54 : 18, 17, 12)
      .fill({ color: this.checked ? UI_COLORS.gold : UI_COLORS.mutedInk, alpha: 0.96 });
  }
}

function createHeading(text: string): Text {
  return new Text({
    text,
    style: {
      fill: UI_COLORS.red,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 20,
      fontWeight: '900'
    }
  });
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 1;
  }

  return Math.min(Math.max(value, 0), 1);
}
